
import React, { useRef, useMemo, useState, useEffect } from "react";
import { useWindowWidth } from "hooks";
import { FlexboxGrid, Alert, Schema } from "rsuite";
import { LS_DIALER_NUMBERS_PER_PAGE } from "const/localStorageKeys";
import {
    MIN_RATE_DIALER,
    SCREEN_MEDIA,
    MODAL_SIZES,
} from "const";
import {
    TRUNK_NUMBER_GET_LIST_API,
    TRUNK_NUMBER_TRANSACTION_ID_API,
    TRUNK_NUMBER_REVOKE_API
} from "const/apiMethods";
import { withTranslation } from "hoc/WithTranslation";
import { api, getFileResponse } from "api/loginRoutes";
import {
    copyToClipboard, getBlobContent, getServiceSmsPrefix,
    resolvePerPage, getServiceByLocation
} from "utils/helpers";


import Page from "components/base/Page";
import PageHeader from "components/base/PageHeader";
import { Spacer } from "components/base/Spacer";
import { FlexGrid, FlexGridItem } from "components/base/FlexGrid";
import { ButtonSecondary, BaseButton } from "components/base/BaseButton";
import ModalAllocationResult from "components/client/Modal/ModalAllocationResult";
import ModalAllocationNumbers from "components/client/Modal/ModalAllocationNumbers";

import TrunkTable from "./TrunkTable";
import NumbersFilters from "./NumbersFilters/NumbersFilters";
import NumbersRevokeModal from "./NumbersRevokeModal";
import AllocatedNumbersTable from "./AllocatedNumbersTable";
import NumbersDownloadActions from "./NumbersDownloadActions";
import m from "definedMessages";
import { useLocation } from "react-router-dom";
import { useNumbersFilters } from "hooks/client/useNumbersFilters";
import { useNumbersSelection } from "hooks/client/useNumbersSelection";

const { StringType, NumberType } = Schema.Types;

const NUMBER_KEY = "trn_key";
const RANGE_KEY = "pr_key";
const SUBDEST_KEY = "sde_key";

const MAX_CHARACTERS = 40;

const { md } = SCREEN_MEDIA;

const defaultFilter = {
    str: "",
    rate_min: MIN_RATE_DIALER,
    rate_max: MIN_RATE_DIALER // MAX_RATE_DIALER
};

const defaultCurrentDisplay = "trunk_number";

const Numbers = ({
    page,
    perPage,
    count,
    service,
    totalNumbersCount,

    trunksList,
    numbersList,

    trunkLoading,
    numbersLoading,

    uploadLoading,
    allocationLoading,

    accountData,
    accountDialerData,

    trunkNumberTransaction,
    outgoingIpForAiTrunk,

    downloadNumbers,
    getTrunkList,
    setTrunkList,
    getTrunkNumbersList,

    showReasonHandler,
    setNumberList,
    setUploadingLoading,
    setTrunkNumberTransaction,
    setAllocatedNumbersLoading,

    formatMessage
}) => {
    const resolvedPerPage = useMemo(() => resolvePerPage(
        LS_DIALER_NUMBERS_PER_PAGE,
        perPage,
    ), [perPage]);

    const tableRef = useRef(null);
    const isFirstFetch = useRef(true);

    const [currentPerPage, setCurrentPerPage] = useState(resolvedPerPage);

    const [firstTrunkId, setFirstTrunkId] = useState(null);
    const [activeTrunkId, setActiveTrunkId] = useState(null);

    const [csvContent, setCsvContent] = useState(null);
    const [trunkAllocatedByOTP, setTrunkAllocatedByOTP] = useState(false);
    const [notAllocatedNumbers, setNotAllocatedNumbers] = useState(null);
    const [allocatedNumbersCount, setAllocatedNumbersCount] = useState(0);

    const [resultLoading, setResultLoading] = useState(false);
    const [numbersRevokeModalLoading, setNumbersRevokeModalLoading] = useState(false);

    const [currentSpKey, setCurrentSpKey] = useState(null);
    const [currentDisplay, setCurrentDisplay] = useState(defaultCurrentDisplay);

    const [showResultModal, setShowResultModal] = useState(null);
    const [showNumbersModal, setShowNumbersModal] = useState(false);
    const [showNumbersRevokeModal, setShowNumbersRevokeModal] = useState(null);
    const [showWarningAllocationMessage, setShowWarningAllocationMessage] = useState(false);

    const getRowKey = () => {
        switch (currentDisplay) {
            case "trunk_number":
                return NUMBER_KEY;
            case "price_range":
                return RANGE_KEY;
            case "subdestination":
                return SUBDEST_KEY;
        }
    };

    const { selectedData, selectedItems, setSelectedData, setSelectedItems, keepSelectedItems } = useNumbersSelection({
        getRowKey
    });

    const { filter, activeFiltersCount, setFilter, onChangeFilters, onSetActiveFiltersCount } = useNumbersFilters({
        perPage,
        service,
        tableRef,
        activeTrunkId,
        defaultFilter,
        currentPerPage,
        currentDisplay,

        setSelectedItems,
        getTrunkNumbersList
    });

    const location = useLocation();

    const resizedWidth = useWindowWidth();
    const isMobile = resizedWidth < md.min;

    const filtersModel = Schema.Model({
        str: StringType().maxLength(MAX_CHARACTERS, formatMessage(m.maxLength, { count: MAX_CHARACTERS })),
        rate_min: NumberType(formatMessage(m.pleaseEnterValidNumber))
            .min(0, formatMessage(m.minNumber, { number: 0 }))
            .max(9.9999, formatMessage(m.maxNumber, { number: 9.9999 })),
        rate_max: NumberType(formatMessage(m.pleaseEnterValidNumber))
            .min(0, formatMessage(m.minNumber, { number: 0 }))
            .max(9.9999, formatMessage(m.maxNumber, { number: 9.9999 })),
    });

    useEffect(() => {
        setCurrentPerPage(resolvedPerPage);
    }, [resolvedPerPage]);

    useEffect(() => {
        setTrunkList([]);
    }, []);

    // Логіка зміни service
    useEffect(() => {
        const serviceByLocation = getServiceByLocation(location);
        const currentService = serviceByLocation !== null ? serviceByLocation : service;

        if (currentService !== null) {
            isFirstFetch.current = true;
            getTrunkList(currentService, () => {
                setActiveTrunkId(null);
                setFirstTrunkId(null);
            });
        }
    }, [service, location]);

    // Логіка componentDidUpdate + componentWillReceiveProps
    useEffect(() => {
        if (!trunksList || trunksList.length === 0) return;

        const filteredTrunks = trunksList.filter(trunk => !trunk.closed);

        // Логіка з componentWillReceiveProps - встановлення першого trunk
        if (filteredTrunks.length && !firstTrunkId) {
            const firstTrunk = filteredTrunks[0];
            setFirstTrunkId(firstTrunk.id);
            setCurrentSpKey(firstTrunk.sp_key);
            setActiveTrunkId(firstTrunk.id);
        }
    }, [trunksList, firstTrunkId]);

    useEffect(() => {
        if (!activeTrunkId || trunksList.length === 0) {
            return;
        }

        if (!isFirstFetch.current) {
            return;
        }

        isFirstFetch.current = false;

        const pickedPerPage = currentPerPage || resolvedPerPage;
        const pickedFilter = filter || defaultFilter;
        const startPage = 1;

        getTrunkNumbersList(
            activeTrunkId,
            service,
            pickedPerPage,
            pickedFilter,
            startPage,
            currentDisplay
        );
    }, [activeTrunkId]);

    // Логіка очищення номерів при новому активному trunkId
    useEffect(() => {
        if (!activeTrunkId || !trunksList.length) {
            setNumberList([]);
            return;
        };
    }, [activeTrunkId]);


    const onCopyList = (text) => {
        copyToClipboard(text);
        Alert.success(formatMessage(m.copiedToClipboard));
    };

    const onGetTrunkNumbers = (trunkId, sp_key) => {
        const pickedPerPage = currentPerPage || resolvedPerPage;
        const pickedFilter = filter || defaultFilter;
        const startPage = 1;

        getTrunkNumbersList(
            trunkId,
            service,
            pickedPerPage,
            pickedFilter,
            startPage,
            currentDisplay
        );

        setCurrentSpKey(sp_key);
        if (trunkId !== activeTrunkId) {
            isFirstFetch.current = true;
            setActiveTrunkId(trunkId);
        }

        setSelectedItems(new Map());
        tableRef.current.clear();
    };

    const setActiveTrunk = (trunkId, allocationLimit) => {
        setActiveTrunkId(trunkId);
    };

    const onDownload = (csvNumberList, trunkId, setUploadingLoading, type = "csv") => {
        const types = ["csv", "txt"];
        const encodedUri = encodeURI(csvNumberList);
        const link = document.createElement("a");

        link.setAttribute("href", encodedUri);
        link.setAttribute("download", `allocated_numbers.${types.includes(type) ? type : "csv"}`);
        document.body.appendChild(link);
        link.click();
        setUploadingLoading(trunkId, false);
    };


    const onDownloadNumbers = (trunkId) => {
        downloadNumbers(trunkId, service);
    };


    const onChangeGroup = (display) => {
        setCurrentDisplay(display);

        if (activeTrunkId) {
            const pickedPerPage = currentPerPage || resolvedPerPage;
            const pickedFilter = filter || defaultFilter;
            const startPage = 1;

            getTrunkNumbersList(
                activeTrunkId,
                service,
                pickedPerPage,
                pickedFilter,
                startPage,
                display
            );
        }

        setSelectedItems(new Map());
        tableRef.current.clear();
    };

    const getItems = (value, nextPerPage = currentPerPage || resolvedPerPage) => {
        if (!activeTrunkId) {
            return;
        }

        setCurrentPerPage(nextPerPage);

        const pickedPerPage = nextPerPage;
        const pickedFilter = filter || defaultFilter;

        getTrunkNumbersList(
            activeTrunkId,
            service,
            pickedPerPage,
            pickedFilter,
            value,
            currentDisplay
        );
    };

    const getRowKeyList = () => {
        switch (currentDisplay) {
            case "trunk_number":
                return "trn_key_list";
            case "price_range":
                return "pr_key_list";
            case "subdestination":
                return "sde_key_list";
        }
    };

    const getSelectedNumbersCount = () => {
        const { all } = selectedData;

        if (all)
            return totalNumbersCount;

        const rowKey = getRowKey();
        let key = "id";
        switch (rowKey) {
            case NUMBER_KEY:
                key = "numbers_count";
                break;
            case RANGE_KEY:
                key = "price_range_trunk_numbers";
                break;
            case SUBDEST_KEY:
                key = "subdestination_trunk_numbers";
                break;
        }

        let count = 0;
        for (const item of selectedItems) {
            count += item[1][key];
        }

        return count;
    };

    const handleRevokeNumbersSubmit = async () => {
        const { all, list } = selectedData;

        const method = getServiceSmsPrefix(service, TRUNK_NUMBER_REVOKE_API);

        const params = {
            target: {
                [getServiceSmsPrefix(service, "trunk_id")]: activeTrunkId
            }
        };

        if (all) {
            params.filterAll = true;

            if (filter) {
                params.filter = filter;
            }
        }

        if (!all) {
            const key = getRowKeyList();
            params[key] = list;
        }

        setNumbersRevokeModalLoading(true);

        await api(method, params).then(({ trunk_numbers }) => {
            Alert.success(formatMessage(m.numbersWereRevoked, { count: trunk_numbers }))
        });

        setNumbersRevokeModalLoading(false);
        getTrunkList(service, () => setActiveTrunkId(activeTrunkId));
        onGetTrunkNumbers(activeTrunkId);
        setShowNumbersRevokeModal(false);
    };

    const onAllocationGetResult = (response, service = true) => {
        setTrunkNumberTransaction(response.trunk_number_transaction || {});

        const trunkNumberTransaction = response.trunk_number_transaction;
        const notAllocatedNumbers = response.not_allocated_numbers;

        if (trunkNumberTransaction) {
            const transactionId = trunkNumberTransaction.id;
            const allocatedNumbersCount = trunkNumberTransaction.numbers;

            setAllocatedNumbersCount(allocatedNumbersCount);
            setResultLoading(true);

            const numbersMethod = getServiceSmsPrefix(service, TRUNK_NUMBER_GET_LIST_API);

            getFileResponse(numbersMethod, {
                target: {
                    [getServiceSmsPrefix(service, TRUNK_NUMBER_TRANSACTION_ID_API)]: transactionId
                }
            })
                .then((response) => {
                    setAllocatedNumbersLoading(false);

                    if (response) {
                        const csvText = getBlobContent(response);
                        csvText.then((text) => {
                            setShowNumbersModal(false);
                            setCsvContent(text);
                            setShowResultModal(true);
                            setResultLoading(false);
                        });
                    }
                })
        }

        if (notAllocatedNumbers) {
            setNotAllocatedNumbers(notAllocatedNumbers);
        }
    };

    const revokeButton = (
        <BaseButton
            buttonStyle="primary"
            as={!isMobile ? BaseButton : ButtonSecondary}
            onClick={() => {
                setShowNumbersRevokeModal(true);
            }}
            loading={false}
            disabled={!selectedData.list.length && !selectedData.all}
        >
            <>
                {!isMobile
                    ? formatMessage(m.revoke)
                    : formatMessage(m.revokeCountNumbers, { count: selectedData.all ? count : selectedData.list.length })
                }
            </>

        </BaseButton>
    );


    return <Page>
        <FlexboxGrid justify="space-between">
            <FlexboxGrid.Item>
                <PageHeader title={formatMessage(m.trunks)} />
            </FlexboxGrid.Item>
        </FlexboxGrid>

        {/* Trunks */}
        <TrunkTable
            data={trunksList}
            service={service}
            activeTrunk={activeTrunkId}
            loading={trunkLoading || numbersLoading}
            uploadLoading={uploadLoading}
            accountDialerState={accountDialerData}
            outgoingIpForAiTrunk={outgoingIpForAiTrunk}
            onClickTrunk={onGetTrunkNumbers}
            onChangeActiveTrunk={setActiveTrunk}
            onDownloadNumbers={onDownloadNumbers}
            onShowNumbersModal={(trunkId, allocationLimit) => {
                setActiveTrunk(trunkId, allocationLimit);
                setShowNumbersModal(true);
            }}
            formatMessage={formatMessage}
        />

        <Spacer />

        {/* Numbers */}

        <FlexboxGrid align="middle" justify="space-between">
            <FlexboxGrid.Item>
                <PageHeader title={formatMessage(m.allocatedNumbers)} />
            </FlexboxGrid.Item>

            {isMobile &&
                <FlexboxGrid.Item>
                    <NumbersFilters
                        service={service}
                        isMobile={isMobile}
                        filter={filter}
                        filterModel={filtersModel}
                        defaultFilter={defaultFilter}
                        currentDisplay={currentDisplay}
                        activeFiltersCount={activeFiltersCount}
                        allocatedNumbersLoading={numbersLoading}

                        onChangeGroup={onChangeGroup}
                        onChangeFilters={onChangeFilters}
                        formatMessage={formatMessage}
                    />
                </FlexboxGrid.Item>
            }

        </FlexboxGrid>

        {!isMobile &&
            <>
                <FlexGrid justify="space-between">

                    <FlexGridItem>
                        <NumbersFilters
                            service={service}
                            onChangeFilters={onChangeFilters}
                            onChangeGroup={onChangeGroup}
                            filter={filter}
                            filterModel={filtersModel}
                            defaultFilter={defaultFilter}
                            currentDisplay={currentDisplay}
                            allocatedNumbersLoading={numbersLoading}
                            formatMessage={formatMessage}
                        />
                    </FlexGridItem>

                    <FlexGridItem>
                        <NumbersDownloadActions
                            {...{
                                filter,
                                service,
                                currentDisplay,
                                activeTrunkId,
                                uploadLoading,
                                setUploadingLoading,

                                formatMessage
                            }}
                        />

                        {revokeButton}
                    </FlexGridItem>

                </FlexGrid>
                <Spacer />
            </>
        }

        <AllocatedNumbersTable
            data={numbersList}
            tableRef={tableRef}
            loading={numbersLoading}
            useSelectedData={keepSelectedItems}
            {...{
                service,
                page,
                count,
                perPage: currentPerPage || resolvedPerPage,
                currentDisplay,
                getRowKey,
                getItems,
                setSelectedData,

                formatMessage
            }}
        />

        {isMobile &&
            <NumbersDownloadActions
                placement={"topStart"}
                {...{
                    filter,
                    service,
                    currentDisplay,
                    activeTrunkId,
                    uploadLoading,
                    setUploadingLoading,

                    formatMessage
                }}
            />

        }

        {isMobile && (selectedData.list.length || selectedData.all) &&
            <>
                <Spacer size={40} />
                <FlexboxGrid.Item>
                    {revokeButton}
                </FlexboxGrid.Item>
            </>
        }


        {/* Modals */}
        <ModalAllocationNumbers
            show={showNumbersModal}
            selectedTrunkId={activeTrunkId}
            allocationLimit={null}
            loading={resultLoading}
            onClose={() => {
                setShowNumbersModal(false);
            }}
            onSuccess={(result) => {
                const pickedPerPage = currentPerPage || resolvedPerPage;
                const pickedFilter = filter || defaultFilter;
                const startPage = 1;

                onAllocationGetResult(result, service);
                getTrunkList(service, () => setActiveTrunkId(activeTrunkId));
                getTrunkNumbersList(
                    activeTrunkId,
                    service,
                    pickedPerPage,
                    pickedFilter,
                    startPage,
                    currentDisplay
                );

                if (result?.reason_list && Object.keys(result.reason_list).includes("template_max_numbers")) {
                    setShowWarningAllocationMessage(true);
                }
            }}
            trunks={trunksList}
            spKey={currentSpKey}
            showReasonHandler={showReasonHandler}
            service={service}
        />

        <ModalAllocationResult
            show={showResultModal}
            maxWidth={MODAL_SIZES.LARGE}
            onCopy={onCopyList}
            downloadNumbers={(list, id, loading) => onDownload(list, id, loading, "csv")}
            downloadNumbersTxt={(list, id, loading) => onDownload(list, id, loading, "txt")}
            onDownloadNumbers={onDownloadNumbers}
            uploadLoading={uploadLoading}
            loading={allocationLoading || resultLoading}
            trunks={trunksList}
            trunkId={activeTrunkId}
            defaultTrunkId={null}
            trunkAllocatedByOTP={trunkAllocatedByOTP}
            methodName={"Trunks"}
            account={accountData}
            service={service}
            csvContent={csvContent}
            notAllocatedNumbers={notAllocatedNumbers}
            trunkNumberTransaction={trunkNumberTransaction}
            allocatedNumbersCount={allocatedNumbersCount}
            setUploadingLoading={setUploadingLoading}
            showWarningAllocationMessage={showWarningAllocationMessage}
            onClose={() => {
                setCsvContent(null);
                setAllocatedNumbersCount(0);
                setShowWarningAllocationMessage(false);
                setNotAllocatedNumbers(null);
                setShowResultModal(false);
            }}
        />

        <NumbersRevokeModal
            show={showNumbersRevokeModal}
            loading={numbersRevokeModalLoading}
            onClose={() => setShowNumbersRevokeModal(false)}
            getCount={getSelectedNumbersCount}
            onSuccess={() => {
                handleRevokeNumbersSubmit();
            }}
            formatMessage={formatMessage}
        />

    </Page>
};

export default withTranslation(Numbers);